import type { FilterObject } from 'core-system/Table/FilterModel'
import { uniq } from 'lodash-es'
import moment from 'moment'
import type { BenefitHistoryMonth } from 'redux/employee/employeeTypes'
import FormattingUtils from 'shared/FormattingUtils'

export const TRANSIT_DEDUCTION = 'COMMUTER_TRANSIT'
export const PARKING_DEDUCTION = 'COMMUTER_PARKING'

export type AllocationHistoryStatus = 'Paid out' | 'Projected' | 'Pending'

export interface AllocationDeduction {
  type: string
  employeeDeduction?: number | null
  employerContribution?: number | null
}

export interface AllocationBalances {
  transitCycleBalance?: number | null
  parkingCycleBalance?: number | null
}

export interface CurrentAllocationRow {
  key: 'transit' | 'parking'
  label: string
  employeeDeduction: number | null
  employerContribution: number | null
  balance: number | null
}

const deductionAmount = (
  deductions: AllocationDeduction[] | undefined,
  type: string,
  field: 'employeeDeduction' | 'employerContribution'
) => {
  const match = deductions?.find((deduction) => deduction.type === type)
  if (!match || match[field] == null) {
    return null
  }
  return match[field]
}

export const buildCurrentAllocations = (
  deductions: AllocationDeduction[] | undefined,
  balances?: AllocationBalances | null
): CurrentAllocationRow[] => [
  {
    key: 'transit',
    label: 'Transit',
    employeeDeduction: deductionAmount(
      deductions,
      TRANSIT_DEDUCTION,
      'employeeDeduction'
    ),
    employerContribution: deductionAmount(
      deductions,
      TRANSIT_DEDUCTION,
      'employerContribution'
    ),
    balance: balances?.transitCycleBalance ?? null,
  },
  {
    key: 'parking',
    label: 'Parking',
    employeeDeduction: deductionAmount(
      deductions,
      PARKING_DEDUCTION,
      'employeeDeduction'
    ),
    employerContribution: deductionAmount(
      deductions,
      PARKING_DEDUCTION,
      'employerContribution'
    ),
    balance: balances?.parkingCycleBalance ?? null,
  },
]

export const allocationHistoryStatus = (
  entry: BenefitHistoryMonth,
  currentMonth: string
): AllocationHistoryStatus => {
  if (entry.month && entry.month > currentMonth) {
    return 'Projected'
  }
  if (entry.paidOut) {
    return 'Paid out'
  }
  return 'Pending'
}

export const formatAllocationMoney = (cents: number | null | undefined) => {
  if (cents == null) {
    return '—'
  }
  return FormattingUtils.formatDollar(cents)
}

export const employerSubsidyTotal = (entry: BenefitHistoryMonth) => {
  const transit = entry.transitEmployerContribution
  const parking = entry.parkingEmployerContribution
  if (transit == null && parking == null) {
    return null
  }
  return (transit || 0) + (parking || 0)
}

export interface AllocationHistoryRow {
  id: string
  month: string
  monthKey: string
  transitAmount: string
  parkingAmount: string
  employerSubsidy: string
  status: AllocationHistoryStatus
  amount: string
  entry: BenefitHistoryMonth
}

export const ALLOCATION_HISTORY_SEARCH_KEYS = ['month', 'amount', 'status']

export const buildAllocationHistoryRows = (
  history: BenefitHistoryMonth[],
  currentMonth: string
): AllocationHistoryRow[] =>
  history.map((entry, index) => {
    const transitAmount = formatAllocationMoney(
      entry.transitEmployeeDeduction ?? null
    )
    const parkingAmount = formatAllocationMoney(
      entry.parkingEmployeeDeduction ?? null
    )
    const employerSubsidy = formatAllocationMoney(employerSubsidyTotal(entry))
    const status = allocationHistoryStatus(entry, currentMonth)
    return {
      id: `${entry.month || 'unknown'}-${index}`,
      month: entry.month
        ? moment(entry.month, 'YYYY-MM').format('MMM YYYY')
        : '—',
      monthKey: entry.month || '',
      transitAmount,
      parkingAmount,
      employerSubsidy,
      status,
      amount: [transitAmount, parkingAmount, employerSubsidy].join(' '),
      entry,
    }
  })

const STATUS_FILTER_ORDER: AllocationHistoryStatus[] = [
  'Paid out',
  'Projected',
  'Pending',
]

export const allocationHistoryFilters = (
  rows: AllocationHistoryRow[]
): FilterObject[] => {
  const newestFirst = [...rows].sort((left, right) =>
    right.monthKey.localeCompare(left.monthKey)
  )
  const statuses = STATUS_FILTER_ORDER.filter((status) =>
    rows.some((row) => row.status === status)
  )
  return [
    {
      header: 'Month',
      checkboxes: uniq(newestFirst.map((row) => row.month)).map((value) => ({
        label: value,
        checked: false,
        key: 'month',
        value,
      })),
    },
    {
      header: 'Status',
      checkboxes: statuses.map((value) => ({
        label: value,
        checked: false,
        key: 'status',
        value,
      })),
    },
  ]
}

export const employerSubsidyCaption = (entry: BenefitHistoryMonth) => {
  const parts: string[] = []
  if (entry.transitEmployerContribution != null) {
    parts.push(
      `Transit ${FormattingUtils.formatDollar(entry.transitEmployerContribution)}`
    )
  }
  if (entry.parkingEmployerContribution != null) {
    parts.push(
      `Parking ${FormattingUtils.formatDollar(entry.parkingEmployerContribution)}`
    )
  }
  return parts.join(' · ')
}
